import { useMemo } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { annualEquivalent, toKr, type Cadence } from '@heivol-finance/calc';
import {
  projectionApi,
  type ScenarioRow,
  type BucketRow,
  type RetirementExpenseRow,
} from '../lib/data.js';
import { formatOre, formatPercent } from '../lib/format.js';
import { chartColors } from '../lib/chart.js';
import { parsePercents, formatSchedule } from '../lib/projection.js';
import { useProjectionModel } from '../hooks/useProjectionModel.js';
import { PageHeader, Card, StatCard, DeleteButton, TextField, OreField, NumberField, Select } from '../components/ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from '../components/table.js';

const CADENCE_OPTS: { value: Cadence; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'custom', label: 'Other' },
];

export function Projection(): JSX.Element {
  const { data, model, reload } = useProjectionModel();

  const colors = useMemo(() => chartColors(), [data]);

  if (!data || !model) return <PageHeader title="Projection" subtitle="Loading…" />;

  const sc = data.scenario;
  const saveScenario = (patch: Partial<ScenarioRow>) => projectionApi.saveScenario({ ...sc, ...patch }).then(reload);
  const { retirement, liquidity, hasRetirementBudget } = model;

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Projection"
        subtitle={
          <>
            When can you retire? Investment buckets projected against the capital your{' '}
            <span className="font-mono">retirement budget</span> requires.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Retirement possible"
          value={retirement ? `Age ${retirement.age}` : '—'}
          tone={retirement ? 'success' : 'warning'}
          hint={
            retirement
              ? `Year ${retirement.year}`
              : hasRetirementBudget
                ? 'Not reached within the horizon — raise contributions or lower spending'
                : 'Add a retirement budget'
          }
        />
        <StatCard
          label="Required capital"
          value={hasRetirementBudget ? formatOre(model.capital) : '—'}
          tone="ai"
          hint={
            hasRetirementBudget
              ? `${formatOre(model.annualRetirementSpending)}/yr ÷ ${formatPercent(sc.withdrawal_rate)}`
              : 'Add a retirement budget to compute required capital'
          }
        />
        <StatCard
          label="Liquidity goal reached"
          value={liquidity ? `Age ${liquidity.age}` : '—'}
          tone={liquidity ? 'success' : 'default'}
          hint={
            model.liquidityGoal > 0
              ? liquidity
                ? `Year ${liquidity.year}`
                : 'Not reached within the horizon'
              : 'Set a liquidity goal below'
          }
        />
        <StatCard
          label={`Net worth at age ${model.endAge}`}
          value={formatOre(Math.round((model.series.at(-1)?.total ?? 0) * 100))}
          hint={sc.real_or_nominal === 'real' ? "In today's kroner (real)" : 'Nominal'}
        />
      </div>

      <Card className="mb-6 p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-hv-sm font-medium text-hv-text">Net worth to age {model.endAge}</div>
          <Select
            value={sc.real_or_nominal}
            options={[
              { value: 'nominal', label: 'Nominal' },
              { value: 'real', label: 'Real (inflation-adjusted)' },
            ]}
            onChange={(v) => saveScenario({ real_or_nominal: v })}
            className="w-56"
          />
        </div>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={model.series} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
              <defs>
                <linearGradient id="fillBuckets" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.ai} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={colors.ai} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="age" stroke={colors.axis} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}`} />
              <YAxis
                stroke={colors.axis}
                tick={{ fontSize: 11 }}
                width={72}
                tickFormatter={(v: number) => (Math.abs(v) >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${Math.round(v / 1000)}k`)}
              />
              <Tooltip
                formatter={(v: number, name) => [formatOre(Math.round(v * 100)), name]}
                labelFormatter={(l) => `Age ${l}`}
                contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text }}
              />
              <Area type="monotone" dataKey="total" name="Net worth" stroke={colors.ai} strokeWidth={2} fill="url(#fillBuckets)" />
              <Line type="monotone" dataKey="pension" name="Pension/equity" stroke={colors.axis} strokeWidth={1} strokeDasharray="4 3" dot={false} />
              {hasRetirementBudget && (
                <ReferenceLine
                  y={toKr(model.capital)}
                  stroke={colors.success}
                  strokeDasharray="5 4"
                  label={{ value: 'Required capital', position: 'insideTopRight', fill: colors.success, fontSize: 11 }}
                />
              )}
              {model.liquidityGoal > 0 && (
                <ReferenceLine
                  y={toKr(model.liquidityGoal)}
                  stroke={colors.ai}
                  strokeDasharray="2 4"
                  label={{ value: 'Liquidity goal', position: 'insideBottomRight', fill: colors.ai, fontSize: 11 }}
                />
              )}
              {retirement && (
                <ReferenceLine
                  x={retirement.age}
                  stroke={colors.chat}
                  strokeDasharray="5 4"
                  label={{ value: `Retirement ${retirement.age}`, position: 'insideTopLeft', fill: colors.chat, fontSize: 11 }}
                />
              )}
              {liquidity && (
                <ReferenceLine
                  x={liquidity.age}
                  stroke={colors.ai}
                  strokeDasharray="2 4"
                  label={{ value: `Liquidity ${liquidity.age}`, position: 'insideBottomLeft', fill: colors.ai, fontSize: 11 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Scenario assumptions */}
      <Card className="mb-6 p-4">
        <div className="mb-3 text-hv-sm font-medium text-hv-text">Assumptions</div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Labeled label="Current age">
            <NumberField value={sc.start_age} onCommit={(v) => saveScenario({ start_age: Math.round(v) })} />
          </Labeled>
          <Labeled label="Target age">
            <NumberField value={sc.target_age} onCommit={(v) => saveScenario({ target_age: Math.round(v) })} />
          </Labeled>
          <Labeled label="Safe withdrawal rate">
            <NumberField value={sc.withdrawal_rate} step={0.005} suffix="frac" onCommit={(v) => saveScenario({ withdrawal_rate: v })} />
          </Labeled>
          <Labeled label="Inflation">
            <NumberField value={sc.inflation} step={0.005} suffix="frac" onCommit={(v) => saveScenario({ inflation: v })} />
          </Labeled>
          <Labeled label="Liquidity goal">
            <OreField value={sc.liquidity_goal_ore} onCommit={(v) => saveScenario({ liquidity_goal_ore: v })} />
          </Labeled>
          <Labeled label="Pension (opening)">
            <OreField value={sc.pension_opening_ore} onCommit={(v) => saveScenario({ pension_opening_ore: v })} />
          </Labeled>
          <Labeled label="Pension +/yr">
            <OreField value={sc.pension_annual_add_ore} onCommit={(v) => saveScenario({ pension_annual_add_ore: v })} />
          </Labeled>
        </div>
      </Card>

      {/* Retirement budget */}
      <Card className="mb-6 overflow-hidden">
        <SectionBar
          title="Retirement budget"
          onAdd={() =>
            projectionApi.addRetirementExpense({ name: 'New expense', planned_ore: 0, cadence: 'monthly' }).then(reload)
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Name</Th>
                <Th>Frequency</Th>
                <Th right>Amount</Th>
                <Th right>Per month</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.retirementExpenses.map((e: RetirementExpenseRow) => (
                <Tr key={e.id}>
                  <Td>
                    <TextField value={e.name} onCommit={(v) => projectionApi.updateRetirementExpense(e.id, { name: v }).then(reload)} />
                  </Td>
                  <Td>
                    <Select
                      value={e.cadence}
                      options={CADENCE_OPTS}
                      onChange={(v) => projectionApi.updateRetirementExpense(e.id, { cadence: v }).then(reload)}
                    />
                  </Td>
                  <Td right>
                    <OreField value={e.planned_ore} onCommit={(v) => projectionApi.updateRetirementExpense(e.id, { planned_ore: v }).then(reload)} />
                  </Td>
                  <TdNum>{formatOre(Math.round(annualEquivalent(e.planned_ore, e.cadence) / 12))}</TdNum>
                  <Td>
                    <DeleteButton onClick={() => projectionApi.deleteRetirementExpense(e.id).then(reload)} />
                  </Td>
                </Tr>
              ))}
              {data.retirementExpenses.length === 0 && (
                <Tr>
                  <Td colSpan={5}>
                    <span className="text-hv-text-tertiary">
                      Add a retirement budget to compute required capital.
                    </span>
                  </Td>
                </Tr>
              )}
            </tbody>
            {data.retirementExpenses.length > 0 && (
              <tfoot>
                <Tr>
                  <Td>
                    <span className="font-medium text-hv-text">Annual retirement spending</span>
                  </Td>
                  <Td />
                  <Td />
                  <TdNum strong>{formatOre(model.annualRetirementSpending)}</TdNum>
                  <Td />
                </Tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Investment buckets */}
      <Card className="overflow-hidden">
        <SectionBar
          title="Investment buckets"
          onAdd={() =>
            projectionApi
              .addBucket({ name: 'New bucket', opening_value_ore: 0, annual_contribution_ore: 0, return_schedule: [0.07], tax_treatment: 'free' })
              .then(reload)
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Name</Th>
                <Th right>Opening value</Th>
                <Th right>Contribution/yr</Th>
                <Th>Return per year (%)</Th>
                <Th>Tax</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.buckets.map((b: BucketRow) => (
                <Tr key={b.id}>
                  <Td>
                    <TextField value={b.name} onCommit={(v) => projectionApi.updateBucket(b.id, { name: v }).then(reload)} />
                  </Td>
                  <Td right>
                    <OreField value={b.opening_value_ore} onCommit={(v) => projectionApi.updateBucket(b.id, { opening_value_ore: v }).then(reload)} />
                  </Td>
                  <Td right>
                    <OreField value={b.annual_contribution_ore} onCommit={(v) => projectionApi.updateBucket(b.id, { annual_contribution_ore: v }).then(reload)} />
                  </Td>
                  <Td>
                    <TextField
                      value={formatSchedule(b.return_schedule)}
                      onCommit={(v) => projectionApi.updateBucket(b.id, { return_schedule: parsePercents(v) }).then(reload)}
                    />
                  </Td>
                  <Td>
                    <Select
                      value={b.tax_treatment}
                      options={[
                        { value: 'free', label: 'Taxable (stocks)' },
                        { value: 'pension', label: 'Pension (PAL)' },
                      ]}
                      onChange={(v) => projectionApi.updateBucket(b.id, { tax_treatment: v }).then(reload)}
                    />
                  </Td>
                  <Td>
                    <DeleteButton onClick={() => projectionApi.deleteBucket(b.id).then(reload)} />
                  </Td>
                </Tr>
              ))}
              {data.buckets.length === 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <span className="text-hv-text-tertiary">No buckets yet — add a portfolio to project.</span>
                  </Td>
                </Tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
